/**
 * GO Lab Datasets home page — a paginated grid of dataset collections.
 *
 * Mounted at `/v4/go-lab/datasets`. Cards link to
 * `/v4/go-lab/datasets/:collectionId`. The "New Collection" placeholder
 * fires a Sonner toast — actual creation lands in piece B.
 */

import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { ErrorBoundary } from '../../../ui/error-boundary';
import { Button } from '../../../ui/button';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from '../../../ui/pagination';
import { TooltipProvider } from '../../../ui/tooltip';

import { useTaxonomyKeys } from '../hooks/useTaxonomy';
import CollectionCard from './components/home/CollectionCard';
import CreateCollectionModal from './components/home/CreateCollectionModal';
import { useDatasetCollections } from './hooks/useDatasets';

const PAGE_SIZE = 12;

const wrapperClassName =
  'flex h-full flex-col overflow-auto bg-[var(--go-spring)] px-4 py-4 md:px-6 md:py-6 xl:px-8 xl:py-6';

const pageHeading = (
  <div className="space-y-2">
    <h1 className="font-jakarta text-3xl font-extrabold tracking-[-0.04em] text-foreground md:text-4xl">
      Datasets
    </h1>
    <p className="max-w-3xl text-[13px] leading-[1.7] text-muted-foreground">
      Curate dataset collections, track annotation progress, and prepare splits for evaluation.
    </p>
  </div>
);

const renderHeader = (onAddNew: () => void) => (
  <div className="flex items-end justify-between gap-4">
    {pageHeading}
    <Button
      variant="secondary"
      size="sm"
      className="h-8 rounded-md border-input bg-transparent px-3 text-sm font-normal text-foreground shadow-xs hover:bg-accent"
      onClick={onAddNew}
    >
      + Add New Collection
    </Button>
  </div>
);

const NewCollectionCard: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex h-full min-h-[180px] cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border/70 bg-card/40 p-6 text-muted-foreground transition hover:border-border hover:bg-card"
    aria-label="New Collection"
    data-testid="new-collection-card"
  >
    <span className="text-3xl leading-none">+</span>
    <span className="text-sm font-semibold">New Collection</span>
  </button>
);

const DatasetsHomePage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const offset = (page - 1) * PAGE_SIZE;

  const openCreateModal = () => setCreateModalOpen(true);

  const { data, isLoading, isError, refetch } = useDatasetCollections({
    limit: PAGE_SIZE,
    offset,
  });
  const { data: taxonomyKeys } = useTaxonomyKeys();

  const taxonomyNameByKey = useMemo(() => {
    const map = new Map<string, string>();
    (taxonomyKeys || []).forEach((k) => map.set(k.taxonomyKey, k.displayName));
    return map;
  }, [taxonomyKeys]);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  const canPrev = page > 1;
  const canNext = page < totalPages;

  if (isLoading) {
    return (
      <div className={wrapperClassName}>
        <div className="space-y-6">
          {renderHeader(openCreateModal)}
          <div
            className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3"
            data-testid="datasets-loading-grid"
          >
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div
                key={i}
                className="h-44 animate-pulse rounded-lg border border-border/70 bg-card"
              />
            ))}
          </div>
        </div>
        <CreateCollectionModal open={createModalOpen} onOpenChange={setCreateModalOpen} />
      </div>
    );
  }

  if (isError) {
    return (
      <div className={wrapperClassName}>
        <div className="space-y-6">
          {renderHeader(openCreateModal)}
          <div className="flex h-[50vh] flex-col items-center justify-center text-center">
            <p className="mb-4 text-muted-foreground">Failed to load collections.</p>
            <Button variant="link" size="sm" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        </div>
        <CreateCollectionModal open={createModalOpen} onOpenChange={setCreateModalOpen} />
      </div>
    );
  }

  const items = data?.items ?? [];
  const isEmpty = items.length === 0;

  return (
    <div className={wrapperClassName}>
      <TooltipProvider>
        <div className="space-y-6">
          {renderHeader(openCreateModal)}

          {isEmpty ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                <NewCollectionCard onClick={openCreateModal} />
              </div>
              <p className="py-8 text-center text-muted-foreground">
                No collections yet. Create your first collection to begin.
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {/* The dashed "New Collection" placeholder shows only in
                    the empty state — the header-level button is the
                    primary entry point once the grid is populated. */}
                {items.map((summary) => (
                  <Link
                    key={summary.id}
                    to={`/v4/go-lab/datasets/${summary.id}`}
                    className="block rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                    aria-label={`Open ${summary.name}`}
                  >
                    <CollectionCard
                      summary={summary}
                      taxonomyDisplayName={
                        taxonomyNameByKey.get(summary.taxonomyKey) ?? summary.taxonomyKey
                      }
                    />
                  </Link>
                ))}
              </div>

              {totalPages > 1 && (
                <Pagination data-testid="datasets-pagination">
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          if (canPrev) setPage((p) => p - 1);
                        }}
                        aria-disabled={!canPrev}
                        // Disabled state must drop from the tab order
                        // too — `aria-disabled` doesn't remove focusability.
                        tabIndex={canPrev ? undefined : -1}
                        className={!canPrev ? 'pointer-events-none opacity-50' : undefined}
                      />
                    </PaginationItem>
                    <PaginationItem>
                      <span className="px-3 text-sm text-muted-foreground">
                        Page {page} of {totalPages}
                      </span>
                    </PaginationItem>
                    <PaginationItem>
                      <PaginationNext
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          if (canNext) setPage((p) => p + 1);
                        }}
                        aria-disabled={!canNext}
                        tabIndex={canNext ? undefined : -1}
                        className={!canNext ? 'pointer-events-none opacity-50' : undefined}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              )}
            </>
          )}
        </div>
        <CreateCollectionModal open={createModalOpen} onOpenChange={setCreateModalOpen} />
      </TooltipProvider>
    </div>
  );
};

const DatasetsHomePageWithBoundary: React.FC = () => (
  <ErrorBoundary>
    <DatasetsHomePage />
  </ErrorBoundary>
);

export default DatasetsHomePageWithBoundary;
